import { useEffect, useState } from "react"
import { IconType } from "react-icons"
import { IoArrowBack, IoDocumentTextOutline, IoLibraryOutline, IoRibbonOutline, IoWarning } from "react-icons/io5"
import { MdOutlinePayments } from "react-icons/md"
import { FaRegMoneyBill1 } from "react-icons/fa6"
import { AiOutlineSignature } from "react-icons/ai"
import { HStack, Icon, Image, Spacer, Spinner, Stack, VStack } from "@chakra-ui/react"
import { NAV_OPTIONS, NAV_OPTIONS_NO_DEALS } from "@/constants"
import { useDropboxDocuments } from "@/hooks"
import { Deal, DealManager, DealStep, NavOption } from "@/types"
import { getPaymentsClientByEmail, listPaymentsBankAccounts } from "@/api/payments"
import { Button, PulsingDot, Text } from "../ui"

type NavIndicator = "dot" | "warning" | "spinner"

const ViewAsNavItem = ({ label, isActive, onSelect, indicator, onNavItemClick }: { label: string; isActive: boolean; onSelect: () => void; indicator?: NavIndicator; onNavItemClick?: () => void }) => {
  const convertUnderscoresToSpaces = (str: string) => {
    if (!str) return ""
    if (str === NavOption.TAX_CALCULATION) {
      return "Calculate Your Taxes"
    }
    return str
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const getIconForLabel = (label: string): IconType => {
    switch (label) {
      case NavOption.DOCUMENTS:
        return IoDocumentTextOutline
      case NavOption.COMPLIANCE:
        return IoRibbonOutline
      case NavOption.CURRENT_OFFER:
        return AiOutlineSignature
      case NavOption.TAX_CALCULATION:
        return FaRegMoneyBill1
      case NavOption.RESOURCES:
        return IoLibraryOutline
      case NavOption.PAYMENT_CENTER:
        return MdOutlinePayments
      default:
        return IoDocumentTextOutline
    }
  }

  return (
    <Stack
      p={3}
      backgroundColor={isActive ? "whiteAlpha.100" : "transparent"}
      cursor="pointer"
      _hover={{ backgroundColor: "whiteAlpha.100" }}
      onClick={() => { onSelect(); onNavItemClick?.() }}
    >
      <HStack>
        <Icon as={getIconForLabel(label) as React.ElementType} size="sm" color={isActive ? "white" : ""} />
        <Text fontSize="sm" fontWeight="medium" color={isActive ? "white" : "darkBlue.700"}>
          {convertUnderscoresToSpaces(label)}
        </Text>
        {indicator === "dot" && <><Spacer /><PulsingDot /></>}
        {indicator === "warning" && <><Spacer /><Icon as={IoWarning} boxSize={4} color="yellow.400" /></>}
        {indicator === "spinner" && <><Spacer /><Spinner size="xs" color="whiteAlpha.700" /></>}
      </HStack>
    </Stack>
  )
}

interface Props {
  deals: Deal[]
  dealManager: DealManager | null
  currentTab: string | null
  onTabChange: (tab: string) => void
  clientName: string
  clientEmail: string
  onBack: () => void
  onNavItemClick?: () => void
}

export const ViewAsNavigation = ({ deals, dealManager, currentTab, onTabChange, clientName, clientEmail, onBack, onNavItemClick }: Props) => {
  const [hasConnectedBank, setHasConnectedBank] = useState(false)
  const [bankCheckDone, setBankCheckDone] = useState(false)

  const showPaymentCenter = deals.some(d => d.isActive === 1)

  useEffect(() => {
    if (!clientEmail) return
    getPaymentsClientByEmail(clientEmail)
      .then(paymentsClient => {
        if (!paymentsClient) { setBankCheckDone(true); return }
        return listPaymentsBankAccounts(paymentsClient.id)
          .then(accounts => setHasConnectedBank(accounts.length > 0))
          .catch(() => setHasConnectedBank(false))
      })
      .catch(() => setHasConnectedBank(false))
      .finally(() => setBankCheckDone(true))
  }, [clientEmail])

  const showPaymentCenterDot = showPaymentCenter && bankCheckDone && !hasConnectedBank
  const showComplianceDot = deals?.some(deal => deal.currentStep === DealStep.COMPLETE_BC_FORM)

  // NDA Dropbox check for Compliance warning indicator
  const activeDeal = deals.find(d => d.isActive === 1)
  const ndaFormDone = activeDeal && activeDeal.currentStep !== DealStep.COMPLETE_NDA_FORM
  const { data: ndaDocs, isLoading: isLoadingNdaDocs } = useDropboxDocuments(ndaFormDone ? activeDeal?.id : undefined)
  const ndaInDropbox = ndaDocs?.some(doc => doc.name.toLowerCase().includes("eso mutual nda")) ?? false

  const getComplianceIndicator = (): NavIndicator | undefined => {
    if (ndaFormDone && isLoadingNdaDocs) return "spinner"
    if (ndaFormDone && !ndaInDropbox) return "warning"
    if (showComplianceDot) return "dot"
    return undefined
  }

  const baseNavOptions = deals && deals.length > 0 ? NAV_OPTIONS : NAV_OPTIONS_NO_DEALS
  const navOptionsToRender = [...baseNavOptions, ...(showPaymentCenter ? [NavOption.PAYMENT_CENTER] : [])]
  const safeNavOptions = navOptionsToRender.filter(Boolean)

  return (
    <Stack h="100%" bg="darkBlue.200" color="white" pb={10}>
      <Stack px={5} pt={4} pb={4}>
        <HStack>
          <Text fontSize="2xs" color="whiteAlpha.600">Viewing as</Text>
          <Spacer />
          <Button
            variant="ghost"
            size="2xs"
            color="whiteAlpha.600"
            _hover={{ color: "white", bg: "whiteAlpha.100" }}
            onClick={() => { onBack(); onNavItemClick?.() }}
          >
            <IoArrowBack /> Back
          </Button>
        </HStack>
        <Text fontSize="lg" fontWeight="bold" fontFamily="body">
          {clientName}
        </Text>
      </Stack>
      {safeNavOptions.map((option) => {
        let indicator: NavIndicator | undefined
        if (option === NavOption.PAYMENT_CENTER && showPaymentCenterDot) indicator = "dot"
        else if (option === NavOption.COMPLIANCE) indicator = getComplianceIndicator()
        return (
          <ViewAsNavItem
            key={option}
            label={option}
            isActive={currentTab === option}
            onSelect={() => onTabChange(option)}
            indicator={indicator}
            onNavItemClick={onNavItemClick}
          />
        )
      })}
      <Spacer />
      {dealManager && (
        <VStack borderRadius={10} bg="whiteAlpha.100" p={5} ml={5} mr={5}>
          <Text fontSize="2xs" textAlign="center">
            Equity Partner
          </Text>
          <Image
            src={dealManager.profilePicture}
            borderRadius={10}
            boxSize="90px"
            alignSelf="center"
          />
          <Text fontSize="lg" fontWeight="bold" mt={3} textAlign="center">
            {dealManager.firstName} {dealManager.lastName}
          </Text>
          <Text fontSize="xs" textAlign="center">
            {dealManager.email}
          </Text>
        </VStack>
      )}
    </Stack>
  )
}
